// ============================================================================
//  🎧 My Music — playlist configuration
// ============================================================================
// This is the only file you normally need to edit to customize the player.
// Every track uses one of TWO fully copyright-safe sources:
//
//  1) SPOTIFY EMBED — your real favourite music (100% legal)
//       source: { type: 'spotify', spotifyUrl: 'https://open.spotify.com/track/…' }
//     The track is played through Spotify's official embed widget — the
//     licensed way Spotify provides for sharing music on your own website.
//     Requirements:
//       • The track / album / playlist must be PUBLIC on Spotify.
//       • Free Spotify accounts stream 30-second previews, Premium streams
//         full tracks. The playback rights stay with Spotify.
//     You may paste a track, album or playlist URL (or a spotify:track:… URI).
//
//  2) ROYALTY-FREE / CREATIVE COMMONS AUDIO
//       source: { type: 'file', src: 'https://…/file.mp3' }
//     Only use files you are allowed to play publicly (CC0 / CC-BY tracks,
//     royalty-free libraries, or your own recordings). The demo entries below
//     are CC0 (public domain) tracks from the Internet Archive — no
//     attribution required.
//
// `artwork`: square image URL for the cover art. Leave empty ('') to use an
//            automatic gradient cover.
// `duration`: display-only text such as '3:45'. If omitted, the player shows
//             the real length once the audio has loaded.

export interface SpotifySource {
  type: 'spotify';
  /** Public Spotify track/album/playlist URL or `spotify:track:…` URI */
  spotifyUrl: string;
}

export interface FileSource {
  type: 'file';
  /** Direct audio URL (self-hosted or royalty-free CDN) */
  src: string;
}

export interface MusicTrack {
  id: string;
  title: string;
  artist: string;
  album?: string;
  artwork?: string;
  duration?: string;
  source: SpotifySource | FileSource;
}

export type SpotifyKind = 'track' | 'album' | 'playlist';

export const musicTracks: MusicTrack[] = [
  // ---- Demo tracks: CC0 (public domain) music from the Internet Archive ----
  {
    id: 'march-for-honor',
    title: 'March for Honor',
    artist: 'CC0 Instruments',
    album: 'CC0 Instruments, Volume I',
    duration: '3:06',
    source: {
      type: 'file',
      src: 'https://archive.org/download/MarchForHonor/March_For_Honor.mp3',
    },
  },
  {
    id: 'distant-wonders',
    title: 'Distant Wonders',
    artist: 'CC0 Instruments',
    album: 'CC0 Instruments, Volume I',
    duration: '2:13',
    source: {
      type: 'file',
      src: 'https://archive.org/download/MarchForHonor/Distant_Wonders.mp3',
    },
  },
  {
    id: 'what-am-i-even-doing',
    title: 'What Am I Even Doing',
    artist: 'celestialghost',
    album: 'Electric Zone',
    duration: '1:31',
    source: {
      type: 'file',
      src: 'https://archive.org/download/electriczone/what%20am%20i%20even%20doing.mp3',
    },
  },
  {
    id: 'antagonist-a',
    title: 'Antagonist A',
    artist: 'Kevin MacLeod',
    album: 'Sci-Fi Sounds (Public Domain)',
    duration: '2:57',
    source: {
      type: 'file',
      src: 'https://archive.org/download/sci-fi-sounds-public-domain-music/Antagonist%20A.mp3',
    },
  },

  // ---- Example: your real favourite music via Spotify's official embed ----
  // Replace this entry with your own favourite tracks / playlists (they just
  // need to be public on Spotify). The player opens Spotify's official embed
  // widget for this track, which handles all playback and licensing.
  {
    id: 'example-bohemian-rhapsody',
    title: 'Bohemian Rhapsody',
    artist: 'Queen',
    album: 'A Night at the Opera',
    duration: '5:55',
    source: {
      type: 'spotify',
      spotifyUrl: 'https://open.spotify.com/track/4uLU6hMCjMI75M1A2tKUQC',
    },
  },
];
